interface Contact {
  id: string
  contact_type: string
  contact_name: string | null
  relationship: string
  loyalty: number
  is_best_friend: boolean
  gm_notes: string | null
  is_revealed: boolean
}

interface Props {
  character: {
    id: string
    contacts?: Contact[]
  }
}

function loyaltyColor(loyalty: number): string {
  if (loyalty >= 70) return 'bg-success'
  if (loyalty >= 50) return 'bg-info'
  if (loyalty >= 30) return 'bg-warning'
  if (loyalty >= 10) return 'bg-orange-500'
  return 'bg-danger'
}

function bandLabel(band: string): string {
  const labels: Record<string, string> = {
    hostile: 'Enemy', strained: 'Unfriendly', neutral: 'Acquaintance', close: 'Friend',
    Enemy: 'Enemy', Unfriendly: 'Unfriendly', Acquaintance: 'Acquaintance',
    Friend: 'Friend', 'Close Friend': 'Close Friend',
  }
  return labels[band] ?? band
}

export function ContactsPanel({ character }: Props) {
  const contacts = character.contacts ?? []

  if (contacts.length === 0) {
    return (
      <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
        <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Contacts</h3>
        <p className="text-sm text-ink-light">No contacts recorded.</p>
      </div>
    )
  }

  // Sort: best friend first, then by loyalty descending
  const sorted = [...contacts].sort((a, b) => {
    if (a.is_best_friend && !b.is_best_friend) return -1
    if (!a.is_best_friend && b.is_best_friend) return 1
    return b.loyalty - a.loyalty
  })

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">Contacts ({contacts.length})</h3>
      <div className="space-y-2">
        {sorted.map(c => (
          <div key={c.id} className="border-b border-parchment/50 pb-2">
            <div className="flex items-center gap-2 text-sm">
              {/* Loyalty bar */}
              <div className="w-16 bg-parchment-dark rounded h-2 flex-shrink-0">
                <div className={`${loyaltyColor(c.loyalty)} rounded h-2`}
                  style={{ width: `${c.loyalty}%` }} />
              </div>
              {/* Name + badges */}
              <div className="flex-1 min-w-0">
                <span className="font-medium">{c.contact_name ?? 'Unknown'}</span>
                {c.is_best_friend && (
                  <span className="ml-1 px-1.5 py-0.5 bg-accent text-white text-xs rounded">BF</span>
                )}
                <span className="ml-1 text-xs text-ink-light">{bandLabel(c.relationship)}</span>
              </div>
              {/* Type */}
              <span className="text-xs text-ink-light flex-shrink-0">{c.contact_type}</span>
            </div>
            {c.gm_notes && (
              <p className="text-xs text-ink-light italic mt-0.5 ml-[72px]">{c.gm_notes}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
